// @ts-nocheck
import { Div } from '@kizi/core-components';
import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

interface PopperProps {
  children: ReactNode;
  content: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  trigger?: 'click' | 'hover';
  containerStyles?: React.CSSProperties;
}

export const PopoverV2: React.FC<PopperProps> = ({
  children,
  content,
  placement = 'bottom',
  trigger = 'click',
  containerStyles = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPlacement, setCurrentPlacement] = useState(placement);
  const triggerRef = useRef<HTMLElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const togglePopper = () => {
    setIsOpen(!isOpen);
  };

  const adjustPosition = () => {
    if (!popperRef.current || !triggerRef.current) return;

    const popperRect = popperRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const fitsTop = triggerRect.top >= popperRect.height;
    const fitsBottom = viewportHeight - triggerRect.bottom >= popperRect.height;
    const fitsLeft = triggerRect.left >= popperRect.width;
    const fitsRight = viewportWidth - triggerRect.right >= popperRect.width;

    if (
      (currentPlacement === 'top' && !fitsTop && !fitsBottom) ||
      (currentPlacement === 'bottom' && !fitsTop && !fitsBottom) ||
      (currentPlacement === 'left' && !fitsLeft && !fitsRight) ||
      (currentPlacement === 'right' && !fitsLeft && !fitsRight)
    ) {
      return; // Stop switching if neither direction fits
    }

    if (popperRect.top < 0 && currentPlacement !== 'bottom' && fitsBottom) {
      setCurrentPlacement('bottom');
    } else if (
      popperRect.bottom > viewportHeight &&
      currentPlacement !== 'top' &&
      fitsTop
    ) {
      setCurrentPlacement('top');
    } else if (
      popperRect.left < 0 &&
      currentPlacement !== 'right' &&
      fitsRight
    ) {
      setCurrentPlacement('right');
    } else if (
      popperRect.right > viewportWidth &&
      currentPlacement !== 'left' &&
      fitsLeft
    ) {
      setCurrentPlacement('left');
    }
  };

  // Intersection Observer callback
  const observerCallback = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (entry.target === triggerRef.current && entry.isIntersecting) {
        setIsOpen(true); // Open popper if trigger is visible
      } else if (!entry.isIntersecting) {
        setIsOpen(false); // Close popper if trigger is not visible
      }
      if (entry.target === popperRef.current && entry.isIntersecting) {
        adjustPosition(); // Adjust popper position when it's visible in the window
      }
    });
  };

  useEffect(() => {
    if (isOpen) {
      const observer = new IntersectionObserver(observerCallback, {
        root: null,
        threshold: 0,
      });

      if (triggerRef.current) {
        observer.observe(triggerRef.current);
      }

      if (popperRef.current) {
        observer.observe(popperRef.current);
      }

      return () => {
        if (triggerRef.current) {
          observer.unobserve(triggerRef.current);
        }
        if (popperRef.current) {
          observer.unobserve(popperRef.current);
        }
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        popperRef.current &&
        !popperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const triggerElement = isValidElement(children)
    ? cloneElement(children, {
        ref: triggerRef,
        onClick: trigger === 'click' ? togglePopper : children.props.onClick,
        ...(trigger === 'hover'
          ? {
              onMouseEnter: () => setIsOpen(true),
              onMouseLeave: () => setIsOpen(false),
            }
          : {}),
      })
    : children;

  const positionStyles: Record<string, React.CSSProperties> = {
    top: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: '15px',
      zIndex: 10,
    },
    bottom: {
      position: 'absolute',
      top: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginTop: '15px',
      zIndex: 10,
    },
    left: {
      position: 'absolute',
      right: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginRight: '15px',
      zIndex: 10,
    },
    right: {
      position: 'absolute',
      left: '100%',
      top: '50%',
      transform: 'translateY(-50%)',
      marginLeft: '15px',
      zIndex: 10,
    },
  };

  const arrowStyles: Record<string, React.CSSProperties> = {
    top: {
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0',
      height: '0',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid #ffffff',
    },
    bottom: {
      position: 'absolute',
      top: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '0',
      height: '0',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid #ffffff',
    },
    left: {
      position: 'absolute',
      right: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '0',
      height: '0',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid #ffffff',
    },
    right: {
      position: 'absolute',
      left: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '0',
      height: '0',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid #ffffff',
    },
  };

  const boxShadowByPlacement: Record<string, string> = {
    bottom: '0px 16px 24px 0px #4E627E29, 0px -4px 10px rgba(0, 0, 0, 0.2)',
    top: '0px 16px 24px 0px #4E627E29, 0px 4px 10px rgba(0, 0, 0, 0.2)',
    right: '0px 16px 24px 0px #4E627E29, -4px 0px 10px rgba(0, 0, 0, 0.2)',
    left: '0px 16px 24px 0px #4E627E29, 4px 0px 10px rgba(0, 0, 0, 0.2)',
  };

  return (
    <Div style={{ position: 'relative', display: 'inline-block' }}>
      {triggerElement}
      {isOpen && (
        <Div
          ref={popperRef}
          style={{
            ...positionStyles[currentPlacement],
            width: '390px',
            boxSizing: 'border-box',
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '16px',
            boxShadow: boxShadowByPlacement[currentPlacement],
            ...containerStyles,
          }}
        >
          {content}
          <Div style={arrowStyles[currentPlacement]} />
        </Div>
      )}
    </Div>
  );
};
